import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Clock, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function History() {
  const { user, loading } = useAuth();
  const [items, setItems] = useState(null);
  const [err, setErr] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login", { state: { from: "/history" }, replace: true });
      return;
    }
    axios
      .get(`${API}/history`, { withCredentials: true })
      .then((r) => setItems(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Errore"));
  }, [user, loading, navigate]);

  return (
    <div className="min-h-screen" data-testid="history-page">
      <header className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="font-heading text-xl font-semibold tracking-tight">
          auto<span className="text-[#0055FF]">.</span>advisor
        </Link>
        <Link to="/wizard" className="text-sm font-medium text-[#0055FF]">Nuova ricerca →</Link>
      </header>

      <main className="max-w-4xl mx-auto px-6 pt-6 pb-24">
        <h1 className="font-heading text-4xl font-semibold tracking-tight">Le tue ricerche</h1>
        <p className="mt-2 text-[#666]">Tutte le raccomandazioni che hai richiesto, sempre a portata di clic.</p>

        <div className="mt-10 space-y-4">
          {err && <p className="text-red-600 text-sm">{String(err)}</p>}
          {items === null && !err && <p className="text-[#666]">Caricamento…</p>}
          {items && items.length === 0 && (
            <div className="rounded-2xl border border-[#EAEAEA] bg-white p-10 text-center">
              <p className="text-[#666]">Non hai ancora effettuato ricerche.</p>
              <Link to="/wizard" className="mt-4 inline-block text-[#0055FF] font-medium">Inizia ora →</Link>
            </div>
          )}
          {items?.map((it, i) => (
            <motion.div
              key={it.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className="rounded-2xl border border-[#EAEAEA] bg-white p-5 flex items-center justify-between gap-4"
              data-testid={`history-item-${i}`}
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#F2F4F8] flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[#0055FF]" />
                </div>
                <div>
                  <p className="font-heading text-lg font-medium">
                    {it.recommendations?.length || 0} auto suggerite
                  </p>
                  <p className="text-sm text-[#666] mt-1">
                    {new Date(it.created_at).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" })}
                    {" · "}
                    Budget {new Intl.NumberFormat("it-IT").format(it.preferences?.budget_eur || 0)} €
                    {" · "}{it.preferences?.fuel_type} · {it.preferences?.brand_tier}
                  </p>
                </div>
              </div>
              <Link
                to={`/results/${it.id}`}
                className="inline-flex items-center gap-1 text-sm font-medium text-[#111] hover:text-[#0055FF]"
                data-testid={`history-open-${i}`}
              >
                Apri <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          ))}
        </div>
      </main>
    </div>
  );
}
